import React, { useCallback, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useTranslate } from 'ts/common/hooks';
import { useClientBookingExperience } from 'client_react/booking/ClientBookingExperience';
import { ROUTE } from 'client_react/booking/common';
import PaymentOptions from 'client_react/booking/components/PaymentOptions';
import PaymentSummary from 'client_react/booking/components/PaymentSummary';
import {
    isBookingSessionError,
    isBookingSessionRequiresActionResponse
} from 'client_react/booking/useBookingSession';
import ClientPaymentForm from 'client_react/common/components/ClientPaymentForm';
import type { ClientPaymentOption } from 'client_react/common/components/ClientPaymentOptions';
import { isContractError } from 'client_react/common/hooks/useContract';

const PaymentForm = ({
    paymentAmountType,
    setPaymentAmountType
}: {
    paymentAmountType: ClientPaymentOption;
    setPaymentAmountType: (x: ClientPaymentOption) => void;
}) => {
    const {
        bookingSession,
        contract,
        isPending,
        navigateWithSession,
        signatureData,
        signContract,
        submitPayment
    } = useClientBookingExperience();

    const t = useTranslate('booking.payment');
    const stripe = useStripe();
    const elements = useElements();

    const errorToast = useToast({
        status: 'error'
    });

    const [isReadyToPay, setIsReadyToPay] = useState(false);

    const handlePaymentFormReady = useCallback(() => {
        setIsReadyToPay(true);
    }, []);

    const handleClickBack = useCallback(() => {
        if (bookingSession?.contractId) {
            navigateWithSession(ROUTE.CONTRACT);
        } else {
            navigateWithSession(ROUTE.CONTACT_INFORMATION);
        }
    }, [bookingSession?.contractId, navigateWithSession]);

    const handleSubmit = useCallback(
        async (
            event: React.FormEvent | { paymentIntentId: string } | { setupIntentId: string }
        ) => {
            if (!stripe || !elements) {
                return;
            }

            const paymentIntentId = 'paymentIntentId' in event ? event.paymentIntentId : undefined;
            const setupIntentId = 'setupIntentId' in event ? event.setupIntentId : undefined;
            let paymentMethodId: string | undefined;

            if (!paymentIntentId && !setupIntentId) {
                const { error: elementsError } = await elements.submit();

                if (elementsError) {
                    errorToast({
                        title: elementsError.message
                    });
                    return;
                }

                const paymentMethodResponse = await stripe.createPaymentMethod({
                    elements
                });

                if (paymentMethodResponse.error) {
                    errorToast({
                        // TODO Make sure this error message is user-friendly
                        title: paymentMethodResponse.error.message
                    });
                    return;
                }

                paymentMethodId = paymentMethodResponse.paymentMethod.id;
            }

            const submitPaymentResponse = await submitPayment({
                paymentAmountType,
                paymentIntentId,
                paymentMethodId,
                setupIntentId
            });

            if (isBookingSessionRequiresActionResponse(submitPaymentResponse)) {
                // This payment intent requires user verification (3D Secure, etc.)
                // So we need to handle that and send the confirmed payment intent id back to our payment endpoint.
                // The API error response detail property is a string that looks like this
                // requires_action:pi_xxxxxxxxxxxxxxxxxxxxxxxx_secret_xxxxxxxxxxxxxxxxxxxxxxxxx
                const clientSecret = submitPaymentResponse.detail.substring(
                    'requires_action:'.length
                );

                if (!(clientSecret.startsWith('pi_') || clientSecret.startsWith('seti_'))) {
                    errorToast({
                        // TODO Show a more specific or accurate error message
                        title: t('error.cardDeclined')
                    });
                    return;
                }

                const { error, paymentIntent, setupIntent } = await stripe.handleNextAction({
                    clientSecret
                });

                if (
                    error ||
                    (paymentIntent && paymentIntent.status !== 'succeeded') ||
                    (setupIntent && setupIntent.status !== 'succeeded')
                ) {
                    errorToast({
                        // TODO Show a more specific or accurate error message
                        title: t('error.cardDeclined')
                    });
                } else if (paymentIntent) {
                    // Resubmit the payment with the confirmed paymentIntentId:
                    await handleSubmit({
                        paymentIntentId: paymentIntent.id
                    });
                } else if (setupIntent) {
                    // Resubmit the payment with the confirmed setupIntentId:
                    await handleSubmit({
                        setupIntentId: setupIntent.id
                    });
                }
            } else if (isBookingSessionError(submitPaymentResponse)) {
                errorToast({
                    // TODO Show a more specific or accurate error message
                    title: t('error.cardDeclined')
                });
            } else {
                // Sign contract after successful payment
                if (signatureData && contract) {
                    const contractSignatureResponse = await signContract({
                        data: {
                            email: contract.contacts[0].email,
                            signaturePaths: signatureData.signaturePaths,
                            svgViewbox: signatureData.svgViewbox,
                            state: contract.state,
                            type: 'contract-signature'
                        }
                    });

                    if (!contractSignatureResponse || isContractError(contractSignatureResponse)) {
                        errorToast({
                            title: t('error.contractSigningFailed')
                        });
                        return;
                    }
                }

                navigateWithSession(ROUTE.CONFIRMATION);
            }
        },
        [
            contract,
            elements,
            errorToast,
            navigateWithSession,
            paymentAmountType,
            signContract,
            signatureData,
            stripe,
            submitPayment,
            t
        ]
    );

    return (
        <ClientPaymentForm
            backButtonText={t('back')}
            billingInformationFields={<PaymentElement onReady={handlePaymentFormReady} />}
            billingInformationTitle={t('billingInformation')}
            isSubmitDisabled={!stripe || !elements || !isReadyToPay || isPending}
            onClickBack={handleClickBack}
            onSubmit={handleSubmit}
            paymentOptions={
                <PaymentOptions
                    paymentAmountType={paymentAmountType}
                    setPaymentAmountType={setPaymentAmountType}
                />
            }
            paymentSummary={<PaymentSummary paymentAmountType={paymentAmountType} />}
            submitButtonText={t('bookSession')}
        />
    );
};

export default PaymentForm;
